import { DollarSign, Hash, MessageSquare, Zap } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/common/ui/card";
import { Skeleton } from "@/common/ui/skeleton";

import { formatCost, formatTokenCount } from "./format";
import type { MetricsSummary } from "./types";

interface MetricsSummaryCardsProps {
  summary: MetricsSummary | null;
  isLoading: boolean;
}

interface MetricCardConfig {
  title: string;
  key: keyof MetricsSummary;
  icon: React.ReactNode;
  format: (value: number) => string;
}

const METRIC_CARDS: MetricCardConfig[] = [
  {
    title: "Total Requests",
    key: "total_requests",
    icon: <MessageSquare className="h-4 w-4 text-muted-foreground" />,
    format: (v) => v.toLocaleString(),
  },
  {
    title: "Est. Input Tokens",
    key: "input_tokens",
    icon: <Zap className="h-4 w-4 text-muted-foreground" />,
    format: formatTokenCount,
  },
  {
    title: "Est. Output Tokens",
    key: "output_tokens",
    icon: <Hash className="h-4 w-4 text-muted-foreground" />,
    format: formatTokenCount,
  },
  {
    title: "Estimated Cost",
    key: "estimated_cost_usd",
    icon: <DollarSign className="h-4 w-4 text-muted-foreground" />,
    format: formatCost,
  },
];

export function MetricsSummaryCards({
  summary,
  isLoading,
}: MetricsSummaryCardsProps): React.ReactElement {
  return (
    <div
      data-testid="metrics-summary-cards"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      {METRIC_CARDS.map((card) => (
        <Card key={card.key}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
            {card.icon}
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton data-testid={`metric-skeleton-${card.key}`} className="h-8 w-20" />
            ) : (
              <div data-testid={`metric-${card.key}`} className="text-2xl font-bold">
                {summary !== null ? card.format(summary[card.key]) : "\u2014"}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
